import { NgClass, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { UserDataType } from '@core/models/types/user-data.type';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { Confirmations } from '@fuse/services/confirmation/confirmations.config';
import { firstValueFrom, switchMap, take } from 'rxjs';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    RouterLink,
    TitleCasePipe,
    NgClass,
  ],
})
export class UserComponent {
  // S4. The auth provider is reached through a dynamic import at the click
  // (the pace.effects/S2 pattern): the shell widget keeps no compile-time
  // edge into the auth feature, and the service stays the same root
  // singleton the sign-in flow uses.
  private injector = inject(Injector);
  private confirmation = inject(FuseConfirmationService);

  user: InputSignal<UserDataType | undefined> = input<
    UserDataType | undefined
  >();
  readonly showAvatar = input(true);
  readonly place = input<'default' | 'sidebar'>('default');

  public isOpen: OutputEmitterRef<boolean> = output();

  toggleMenu(event: boolean) {
    this.isOpen.emit(event);
  }

  logout(): void {
    const dialogRef = this.confirmation.open(Confirmations.logout);
    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        switchMap(async (result) => {
          if (result === 'confirmed') {
            const { AuthDataProviderService } =
              await import('@app/features/auth/_common/services/auth-data-provider.service');
            return firstValueFrom(
              this.injector.get(AuthDataProviderService).logout()
            );
          }
          return void 0;
        })
      )
      .subscribe((): undefined => undefined);
  }
}
